import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import AdminLayout from '../../components/Layout/AdminLayout';
import { useAuth } from '../../context/AuthContext';
import { api } from '../../services/api';
import type { AdminApplicationDetail, ApplicationStatus, AdminUser, AdminPrequalificationDetail, ReviewerScoreSummary, ReviewCriterion, ExclusionReview, ExclusionDecideRequest, AiSummary, AiSummaryData, ComplianceCheck } from '@vedjegy/shared';
import { FORM_FIELD_LABELS } from '../application/ApplicationFormPage';
import type { UploadDownloadItem } from '../../services/api';
import { APPLICATION_STATUS_LABELS, APPLICATION_STATUS_CONFIG } from '../../utils/applicationStatus';
import { getAppYear } from '../../utils/appYear';

// Human-readable Hungarian labels for upload types
const UPLOAD_TYPE_LABELS: Record<string, string> = {
  section_file:   'Szekció melléklet',
  logo:           'Logó',
  photo:          'Fénykép',
  recommendation: 'Ajánlás',
  certificate:    'Tanúsítvány',
  award:          'Díj / elismerés',
  other:          'Egyéb',
};

const ALL_STATUSES: ApplicationStatus[] = [
  'draft', 'submitted', 'preq_under_review', 'preq_passed', 'preq_failed',
  'under_review', 'deficiency_required', 'deficiency_submitted',
  'completed', 'approved', 'rejected', 'board_rejected', 'board_approved',
  'pending_exclusion_review', 'excluded',
];

const STATUS_LABELS = APPLICATION_STATUS_LABELS;
const STATUS_COLORS = APPLICATION_STATUS_CONFIG;

// Allowed next statuses for tenant_admin — super_admin bypasses this
const NEXT_STATUSES: Partial<Record<ApplicationStatus, ApplicationStatus[]>> = {
  submitted:                 ['preq_under_review', 'preq_passed', 'preq_failed'],
  preq_under_review:         ['preq_passed', 'preq_failed'],
  preq_passed:               ['under_review'],
  under_review:              ['deficiency_required', 'completed', 'approved', 'rejected'],
  deficiency_submitted:      ['under_review', 'completed', 'approved', 'rejected'],
  // Board overrides: admin can apply board decision after reviewer verdict
  approved:                  ['board_rejected'],
  rejected:                  ['board_approved'],
  // Exclusion review: decision handled via dedicated panel (not status buttons)
  pending_exclusion_review:  [],
};

function formatDate(iso: string | null): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('hu-HU', {
    year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit',
  });
}

export default function AdminApplicationDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isSuperAdmin = user?.roles.includes('super_admin') ?? false;

  const [app, setApp]             = useState<AdminApplicationDetail | null>(null);
  const [reviewers, setReviewers] = useState<AdminUser[]>([]);
  const [loading, setLoading]     = useState(true);
  const [error, setError]         = useState('');

  const [statusChanging, setStatusChanging]             = useState(false);
  const [confirmReset, setConfirmReset]                 = useState(false);
  const [assigning, setAssigning]                       = useState(false);
  const [selectedReviewerId, setSelectedReviewerId]     = useState('');
  const [actionError, setActionError]                   = useState('');

  const [preqDetail, setPreqDetail]         = useState<AdminPrequalificationDetail | null>(null);
  const [preqExpanded, setPreqExpanded]     = useState(false);
  const [preqLoading, setPreqLoading]       = useState(false);

  const [uploads, setUploads]               = useState<UploadDownloadItem[]>([]);
  const [uploadsLoaded, setUploadsLoaded]   = useState(false);

  const [criteria, setCriteria]             = useState<ReviewCriterion[]>([]);

  // AI summary (read + generate for admins; no score acceptance)
  const [aiSummary, setAiSummary]       = useState<AiSummary | null>(null);
  const [aiLoaded, setAiLoaded]         = useState(false);
  const [aiGenerating, setAiGenerating] = useState(false);
  const [aiError, setAiError]           = useState('');
  const [aiExpanded, setAiExpanded]     = useState<Set<string>>(new Set());

  const [exclusionReview, setExclusionReview]         = useState<ExclusionReview | null>(null);
  const [exclusionDecision, setExclusionDecision]     = useState<ExclusionDecideRequest['decision'] | ''>('');
  const [exclusionReason, setExclusionReason]         = useState('');
  const [exclusionSaving, setExclusionSaving]         = useState(false);
  const [exclusionError, setExclusionError]           = useState('');
  const [exclusionSuccess, setExclusionSuccess]       = useState('');

  const [excellentSaving, setExcellentSaving]         = useState(false);
  const [excellentError, setExcellentError]           = useState('');

  const [compliance, setCompliance]                   = useState<ComplianceCheck | null>(null);
  const [complianceLoaded, setComplianceLoaded]       = useState(false);
  const [complianceRechecking, setComplianceRechecking] = useState(false);
  const [complianceError, setComplianceError]         = useState('');

  useEffect(() => {
    if (!id) return;
    setLoading(true);
    Promise.all([
      api.admin.getApplication(id),
      api.admin.listUsers(),
      api.admin.getUploads(id),
      api.admin.getCriteria(),
    ]).then(([appData, usersData, uploadsData, criteriaData]) => {
      setApp(appData);
      setReviewers(usersData.users.filter((u) => u.roles.includes('reviewer')));
      setUploads(uploadsData.uploads);
      setUploadsLoaded(true);
      setCriteria(criteriaData.criteria);
      // Load exclusion review if applicable
      if (appData.status === 'pending_exclusion_review' || appData.status === 'excluded') {
        return api.admin.getExclusionReview(id).then((d) => setExclusionReview(d.review));
      }
    }).catch(() => setError('Nem sikerült betölteni a pályázatot.')).finally(() => setLoading(false));
  }, [id]);

  // Load AI summary once on mount
  useEffect(() => {
    if (!id || aiLoaded) return;
    setAiLoaded(true);
    api.ai.getSummary(id)
      .then((res) => setAiSummary(res.summary))
      .catch(() => { /* no summary yet */ });
  }, [id, aiLoaded]);

  // Poll while pending/running
  useEffect(() => {
    if (!id) return;
    if (!aiSummary || !['pending', 'running'].includes(aiSummary.status)) return;
    const t = setInterval(() => {
      api.ai.getSummary(id)
        .then((res) => setAiSummary(res.summary))
        .catch(() => { /* silent */ });
    }, 2000);
    return () => clearInterval(t);
  }, [id, aiSummary?.status]);

  // Load compliance check once on mount
  useEffect(() => {
    if (!id || complianceLoaded) return;
    setComplianceLoaded(true);
    api.compliance.get(id)
      .then((res) => setCompliance(res.check))
      .catch(() => { /* no check yet */ });
  }, [id, complianceLoaded]);

  async function handleComplianceRecheck() {
    if (!id) return;
    setComplianceRechecking(true);
    setComplianceError('');
    try {
      await api.compliance.recheck(id);
      // Poll until new result appears (may take up to ~60s for NRMK download)
      const poll = setInterval(() => {
        api.compliance.get(id)
          .then((res) => {
            if (res.check && res.check.id !== compliance?.id) {
              setCompliance(res.check);
              clearInterval(poll);
              setComplianceRechecking(false);
            }
          })
          .catch(() => { /* silent */ });
      }, 3000);
      // Safety timeout after 90s
      setTimeout(() => { clearInterval(poll); setComplianceRechecking(false); }, 90_000);
    } catch {
      setComplianceError('Nem sikerült elindítani az ellenőrzést.');
      setComplianceRechecking(false);
    }
  }

  async function generateAiSummary() {
    if (!id) return;
    setAiGenerating(true);
    setAiError('');
    try {
      await api.ai.triggerSummary(id);
      const res = await api.ai.getSummary(id);
      setAiSummary(res.summary);
    } catch (e) {
      setAiError(e instanceof Error ? e.message : 'Hiba az AI elemzés indítása közben.');
    } finally {
      setAiGenerating(false);
    }
  }

  function toggleAiSection(sectionId: string) {
    setAiExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(sectionId)) next.delete(sectionId);
      else next.add(sectionId);
      return next;
    });
  }

  async function handleStatusChange(newStatus: ApplicationStatus) {
    if (!app || !id) return;
    setStatusChanging(true);
    setActionError('');
    try {
      await api.admin.updateApplicationStatus(id, newStatus);
      setApp((prev) => prev ? { ...prev, status: newStatus } : prev);
    } catch {
      setActionError('Státuszváltás sikertelen.');
    } finally {
      setStatusChanging(false);
    }
  }

  async function handleAssignReviewer() {
    if (!app || !id || !selectedReviewerId) return;
    setActionError('');
    try {
      await api.admin.assignReviewer(id, selectedReviewerId);
      const u = reviewers.find((r) => r.id === selectedReviewerId);
      if (u) setApp((prev) => prev ? { ...prev, reviewers: [...prev.reviewers, { id: u.id, email: u.email }] } : prev);
      setSelectedReviewerId('');
      setAssigning(false);
    } catch {
      setActionError('Hozzárendelés sikertelen.');
    }
  }

  async function handleUnassignReviewer(reviewerId: string) {
    if (!app || !id) return;
    setActionError('');
    try {
      await api.admin.unassignReviewer(id, reviewerId);
      setApp((prev) => prev ? { ...prev, reviewers: prev.reviewers.filter((r) => r.id !== reviewerId) } : prev);
    } catch {
      setActionError('Eltávolítás sikertelen.');
    }
  }

  async function handleExclusionDecide() {
    if (!app || !id || !exclusionDecision) return;
    if (!exclusionReason.trim()) {
      setExclusionError('Az indoklás megadása kötelező.');
      return;
    }
    setExclusionSaving(true);
    setExclusionError('');
    setExclusionSuccess('');
    try {
      const result = await api.admin.decideExclusionReview(id, {
        decision: exclusionDecision as ExclusionDecideRequest['decision'],
        reason: exclusionReason.trim(),
      });
      setApp((prev) => prev ? { ...prev, status: result.newStatus as ApplicationStatus } : prev);
      // Refresh exclusion review record
      const updated = await api.admin.getExclusionReview(id);
      setExclusionReview(updated.review);
      setExclusionSuccess('Döntés mentve.');
    } catch {
      setExclusionError('Döntés mentése sikertelen.');
    } finally {
      setExclusionSaving(false);
    }
  }

  async function handleExcellentDecision(approved: boolean | null) {
    if (!app || !id) return;
    setExcellentSaving(true);
    setExcellentError('');
    try {
      const res = await api.admin.setExcellentApproved(id, approved);
      setApp((prev) => prev ? { ...prev, excellentApproved: res.excellentApproved } : prev);
    } catch {
      setExcellentError('Döntés mentése sikertelen.');
    } finally {
      setExcellentSaving(false);
    }
  }

  async function handleTogglePreq() {
    if (preqExpanded) { setPreqExpanded(false); return; }
    setPreqExpanded(true);
    if (preqDetail) return;
    setPreqLoading(true);
    try {
      const d = await api.admin.getPrequalification(id!);
      setPreqDetail(d);
    } catch {
      // ignore — summary card still shows score
    } finally {
      setPreqLoading(false);
    }
  }

  if (loading) {
    return <AdminLayout><div style={{ padding: '40px', color: 'var(--muted-foreground)' }}>Betöltés...</div></AdminLayout>;
  }
  if (error || !app) {
    return (
      <AdminLayout>
        <div style={{ padding: '40px' }}>
          <div className="alert alert-destructive" style={{ marginBottom: 20 }}><span>warning</span><div><p>{error || 'Pályázat nem található.'}</p></div></div>
          <button className="btn btn-ghost" onClick={() => navigate('/admin/applications')}>Vissza</button>
        </div>
      </AdminLayout>
    );
  }

  const sc = STATUS_COLORS[app.status];
  // super_admin sees all statuses except the current one; tenant_admin sees only valid transitions
  const nextStatuses: ApplicationStatus[] = isSuperAdmin
    ? ALL_STATUSES.filter((s) => s !== app.status)
    : (NEXT_STATUSES[app.status] ?? []);
  const assignedIds = new Set(app.reviewers.map((r) => r.id));
  const availableReviewers = reviewers.filter((r) => !assignedIds.has(r.id));

  return (
    <AdminLayout>
      <div style={{ padding: '32px 40px', maxWidth: 2000 }}>
        {/* Back + header */}
        <button className="btn btn-ghost btn-sm" onClick={() => navigate('/admin/applications')} style={{ marginBottom: 16 }}>
          Pályázók
        </button>
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16, marginBottom: 28 }}>
          <div>
            <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '.12em', textTransform: 'uppercase', color: 'var(--secondary-foreground)', marginBottom: 4 }}>
              PÁLYÁZAT · {app.year} · {app.type === 'new' ? 'Új pályázó' : 'Megújítás'}
            </div>
            <h1 style={{ margin: 0, fontSize: 24, fontWeight: 700, color: 'var(--primary)' }}>{app.companyName}</h1>
            <div style={{ fontSize: 13, color: 'var(--muted-foreground)', marginTop: 4 }}>Adószám: {app.taxId}</div>
          </div>
          <span style={{ display: 'inline-block', padding: '6px 14px', borderRadius: 20, fontSize: 13, fontWeight: 700, background: sc.bg, color: sc.text, marginTop: 24 }}>
            {STATUS_LABELS[app.status]}
          </span>
        </div>

        {actionError && (
          <div className="alert alert-destructive" style={{ marginBottom: 20 }}>
            <span>warning</span><div><p>{actionError}</p></div>
          </div>
        )}

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>

          {/* Info card */}
          <div style={{ background: '#fff', border: '1px solid var(--border)', borderRadius: 'var(--radius-lg)', padding: '24px 28px', boxShadow: 'var(--shadow-sm)' }}>
            <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '.1em', textTransform: 'uppercase', color: 'var(--secondary-foreground)', marginBottom: 16 }}>Adatok</div>
            {[
              { label: 'Beküldve',   value: formatDate(app.submittedAt) },
              { label: 'Létrehozva', value: formatDate(app.createdAt) },
              { label: 'Típus',      value: app.type === 'new' ? 'Új pályázó' : 'Megújítás' },
              { label: 'Év',         value: String(app.year) },
            ].map(({ label, value }) => (
              <div key={label} style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid var(--border)' }}>
                <span style={{ fontSize: 13, color: 'var(--muted-foreground)' }}>{label}</span>
                <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--primary)' }}>{value}</span>
              </div>
            ))}
          </div>

          {/* Status card */}
          <div style={{ background: '#fff', border: '1px solid var(--border)', borderRadius: 'var(--radius-lg)', padding: '24px 28px', boxShadow: 'var(--shadow-sm)' }}>
            <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '.1em', textTransform: 'uppercase', color: 'var(--secondary-foreground)', marginBottom: 16 }}>
              Státuszváltás{isSuperAdmin && <span style={{ marginLeft: 8, fontSize: 10, fontWeight: 400, color: 'var(--muted-foreground)', textTransform: 'none' }}>(összes státusz)</span>}
            </div>
            {nextStatuses.length === 0 ? (
              <p style={{ fontSize: 13, color: 'var(--muted-foreground)', margin: 0 }}>A jelenlegi státuszból nincs elérhető következő lépés.</p>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {nextStatuses.map((s) => {
                  const c = STATUS_COLORS[s];
                  return (
                    <button
                      key={s}
                      className="btn btn-ghost btn-sm"
                      disabled={statusChanging}
                      onClick={() => void handleStatusChange(s)}
                      style={{ justifyContent: 'flex-start', border: '1px solid ' + c.bg, color: c.text, background: c.bg, fontWeight: 700 }}
                    >
                      {STATUS_LABELS[s]}
                    </button>
                  );
                })}
              </div>
            )}
            {/* Reset prequalification — resets to submitted + clears the submission so the applicant can resubmit */}
            {!isSuperAdmin && app.status !== 'preq_under_review' && app.status !== 'submitted' && (
              <div style={{ marginTop: 16, paddingTop: 16, borderTop: '1px solid var(--border)' }}>
                {confirmReset ? (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    <p style={{ fontSize: 12, color: 'var(--muted-foreground)', margin: 0 }}>
                      Visszaállítod az előminősítést? A pályázó újra kitöltheti és beküldheti.
                    </p>
                    <div style={{ display: 'flex', gap: 8 }}>
                      <button
                        className="btn btn-sm"
                        disabled={statusChanging}
                        onClick={() => { void handleStatusChange('submitted'); setConfirmReset(false); }}
                        style={{ background: '#fef9e7', color: '#b07d00', border: '1px solid #fef9e7', fontWeight: 700 }}
                      >
                        Igen, visszaállítás
                      </button>
                      <button className="btn btn-ghost btn-sm" onClick={() => setConfirmReset(false)}>Mégse</button>
                    </div>
                  </div>
                ) : (
                  <button
                    className="btn btn-ghost btn-sm"
                    disabled={statusChanging}
                    onClick={() => setConfirmReset(true)}
                    style={{ fontSize: 12, color: 'var(--muted-foreground)' }}
                  >
                    ↺ Előminősítés visszanyitása
                  </button>
                )}
              </div>
            )}
          </div>

          {/* Prequalification card */}
          {app.prequalification && (
            <div style={{ background: '#fff', border: '1px solid var(--border)', borderRadius: 'var(--radius-lg)', padding: '24px 28px', boxShadow: 'var(--shadow-sm)', gridColumn: '1 / -1' }}>
              {/* Renewal bypass banner */}
              {app.prequalification.renewalBypass && (
                <div style={{
                  display: 'flex', alignItems: 'center', gap: 10,
                  background: 'rgba(59,130,246,.08)', border: '1px solid rgba(59,130,246,.35)',
                  borderRadius: 8, padding: '10px 16px', marginBottom: 16,
                  fontSize: 13, color: '#1e40af', fontWeight: 600,
                }}>
                  <span style={{ fontSize: 16 }}>ℹ️</span>
                  Megújító – előminősítés kihagyva
                </div>
              )}
              {/* Summary row */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 20 }}>
                <div style={{
                  width: 64, height: 64, borderRadius: '50%', flexShrink: 0,
                  background: app.prequalification.passed ? 'rgba(78,192,124,.15)' : 'rgba(209,49,46,.10)',
                  border: '3px solid ' + (app.prequalification.passed ? '#4EC07C' : '#c0392b'),
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  <span style={{ fontSize: 18, fontWeight: 700, color: app.prequalification.passed ? '#064935' : '#c0392b' }}>
                    {app.prequalification.renewalBypass ? '—' : `${app.prequalification.percentage}%`}
                  </span>
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '.1em', textTransform: 'uppercase', color: 'var(--secondary-foreground)', marginBottom: 4 }}>Előminősítés</div>
                  <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--primary)' }}>
                    {app.prequalification.passed ? 'Megfelelt' : 'Nem felelt meg'}
                  </div>
                  <div style={{ fontSize: 13, color: 'var(--muted-foreground)', marginTop: 2 }}>
                    {app.prequalification.renewalBypass
                      ? 'Automatikusan jóváhagyva · Alkalmazottak: ' + (app.prequalification.hasEmployees ? 'van' : 'nincs')
                      : `${app.prequalification.totalScore} / ${app.prequalification.maxScore} pont · Alkalmazottak: ${app.prequalification.hasEmployees ? 'van' : 'nincs'}`
                    }
                  </div>
                </div>
                {!app.prequalification.renewalBypass && (
                  <button
                    className="btn btn-ghost btn-sm"
                    onClick={() => void handleTogglePreq()}
                    style={{ fontSize: 13, color: 'var(--muted-foreground)', flexShrink: 0 }}
                  >
                    {preqExpanded ? '▲ Bezárás' : '▼ Válaszok megtekintése'}
                  </button>
                )}
              </div>

              {/* Expanded answers */}
              {preqExpanded && (
                <div style={{ marginTop: 20, borderTop: '1px solid var(--border)', paddingTop: 16 }}>
                  {preqLoading ? (
                    <div style={{ fontSize: 13, color: 'var(--muted-foreground)' }}>Betöltés...</div>
                  ) : preqDetail ? (
                    <>
                    {/* Online URLs */}
                    {(() => {
                      const u = preqDetail.onlineUrls;
                      const entries: { label: string; url: string }[] = [
                        { label: 'Weboldal',       url: u.websiteUrl          ?? '' },
                        { label: 'Facebook',       url: u.facebookUrl         ?? '' },
                        { label: 'Instagram',      url: u.instagramUrl        ?? '' },
                        { label: 'YouTube',        url: u.youtubeUrl          ?? '' },
                        { label: 'LinkedIn',       url: u.linkedinUrl         ?? '' },
                        { label: 'Egyéb online',   url: u.otherOnlinePresence ?? '' },
                      ].filter((e) => typeof e.url === 'string' && e.url.trim());
                      if (!entries.length) return null;
                      return (
                        <div style={{ marginBottom: 16 }}>
                          <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--muted-foreground)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 8 }}>
                            Online jelenlét
                          </div>
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px 16px' }}>
                            {entries.map(({ label, url }) => (
                              <a key={label} href={url} target="_blank" rel="noopener noreferrer"
                                style={{ fontSize: 13, color: 'var(--primary)', textDecoration: 'underline' }}>
                                {label}
                              </a>
                            ))}
                          </div>
                        </div>
                      );
                    })()}
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                      <thead>
                        <tr style={{ background: 'var(--muted)' }}>
                          <th style={{ padding: '8px 12px', textAlign: 'left', fontWeight: 700, color: 'var(--secondary-foreground)', width: 32 }}>#</th>
                          <th style={{ padding: '8px 12px', textAlign: 'left', fontWeight: 700, color: 'var(--secondary-foreground)' }}>Kérdés</th>
                          <th style={{ padding: '8px 12px', textAlign: 'left', fontWeight: 700, color: 'var(--secondary-foreground)' }}>Válasz</th>
                          <th style={{ padding: '8px 12px', textAlign: 'right', fontWeight: 700, color: 'var(--secondary-foreground)', width: 80 }}>Pont</th>
                        </tr>
                      </thead>
                      <tbody>
                        {preqDetail.answers.map((a) => (
                          <tr key={a.questionNumber} style={{ borderBottom: '1px solid var(--border)', opacity: a.isApplicable ? 1 : 0.45 }}>
                            <td style={{ padding: '10px 12px', color: 'var(--muted-foreground)', fontWeight: 700 }}>{a.questionNumber}</td>
                            <td style={{ padding: '10px 12px', color: 'var(--primary)' }}>{a.questionText}</td>
                            <td style={{ padding: '10px 12px', color: a.isApplicable ? 'var(--primary)' : 'var(--muted-foreground)' }}>
                              {a.isApplicable ? (a.selectedOptionText ?? '—') : 'Nem releváns'}
                            </td>
                            <td style={{ padding: '10px 12px', textAlign: 'right', fontWeight: 700,
                              color: a.isApplicable && a.score !== null
                                ? (a.score === a.maxScore ? '#1a7a40' : a.score === 0 ? '#c0392b' : '#b07d00')
                                : 'var(--muted-foreground)'
                            }}>
                              {a.isApplicable && a.score !== null ? `${a.score} / ${a.maxScore}` : '—'}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    </>
                  ) : (
                    <div style={{ fontSize: 13, color: 'var(--muted-foreground)' }}>Nem sikerült betölteni a válaszokat.</div>
                  )}
                </div>
              )}
            </div>
          )}


          {/* Exclusion review panel — shown when status is pending_exclusion_review or excluded */}
          {(app.status === 'pending_exclusion_review' || (app.status === 'excluded' && exclusionReview)) && exclusionReview && (
            <div style={{
              background: '#fff',
              border: '2px solid #f59e0b',
              borderRadius: 'var(--radius-lg)',
              padding: '24px 28px',
              boxShadow: 'var(--shadow-sm)',
              gridColumn: '1 / -1',
            }}>
              <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '.1em', textTransform: 'uppercase', color: '#92400e', marginBottom: 16 }}>
                Kizárási vizsgálat
              </div>

              {/* Flagged declarations */}
              <div style={{
                background: '#fff3cd',
                border: '1px solid #ffc107',
                borderRadius: 8,
                padding: '14px 18px',
                marginBottom: 20,
              }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: '#856404', marginBottom: 8 }}>
                  Jelzett kizáró nyilatkozat{exclusionReview.flaggedDeclarations.length > 1 ? 'ok' : ''}:
                </div>
                {exclusionReview.flaggedDeclarations.map((d) => (
                  <div key={d.declarationKey} style={{ fontSize: 13, color: '#1e293b', marginBottom: 4, paddingLeft: 8 }}>
                    • {d.label}
                  </div>
                ))}
              </div>

              {/* Previous decision (read-only display) */}
              {exclusionReview.decision && (
                <div style={{
                  background: exclusionReview.decision === 'accepted' ? '#f0fdf4' :
                               exclusionReview.decision === 'rejected' ? '#fef2f2' : '#fffbeb',
                  border: '1px solid ' + (exclusionReview.decision === 'accepted' ? '#bbf7d0' :
                               exclusionReview.decision === 'rejected' ? '#fca5a5' : '#fde68a'),
                  borderRadius: 8,
                  padding: '14px 18px',
                  marginBottom: 20,
                }}>
                  <div style={{ fontSize: 12, fontWeight: 700, marginBottom: 6,
                    color: exclusionReview.decision === 'accepted' ? '#166534' :
                           exclusionReview.decision === 'rejected' ? '#b91c1c' : '#92400e',
                  }}>
                    Döntés:{' '}
                    {exclusionReview.decision === 'accepted' ? 'Befogadva' :
                     exclusionReview.decision === 'rejected' ? 'Kizárva' : 'Pontosítás szükséges'}
                  </div>
                  {exclusionReview.reason && (
                    <div style={{ fontSize: 13, color: '#374151', marginBottom: 6, whiteSpace: 'pre-wrap' }}>
                      {exclusionReview.reason}
                    </div>
                  )}
                  {exclusionReview.decidedByEmail && exclusionReview.decidedAt && (
                    <div style={{ fontSize: 11, color: 'var(--muted-foreground)' }}>
                      {exclusionReview.decidedByEmail} · {new Date(exclusionReview.decidedAt).toLocaleString('hu-HU')}
                    </div>
                  )}
                </div>
              )}

              {/* Decision form — only shown when still pending */}
              {app.status === 'pending_exclusion_review' && (
                <>
                  <div style={{ marginBottom: 16 }}>
                    <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--primary)', marginBottom: 10 }}>Döntés:</div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                      {([
                        { value: 'accepted',              label: 'Befogadható — a pályázat folytatódhat', color: '#166534', bg: '#f0fdf4' },
                        { value: 'rejected',              label: 'Elutasítva — a pályázat kizárásra kerül', color: '#b91c1c', bg: '#fef2f2' },
                        { value: 'clarification_needed',  label: 'Pontosítás szükséges — visszajelzés küldése', color: '#92400e', bg: '#fffbeb' },
                      ] as const).map((opt) => (
                        <label
                          key={opt.value}
                          style={{
                            display: 'flex', alignItems: 'center', gap: 10,
                            padding: '10px 14px',
                            borderRadius: 8,
                            border: '1px solid ' + (exclusionDecision === opt.value ? opt.color : 'var(--border)'),
                            background: exclusionDecision === opt.value ? opt.bg : '#fff',
                            cursor: 'pointer',
                          }}
                        >
                          <input
                            type="radio"
                            name="exclusion_decision"
                            value={opt.value}
                            checked={exclusionDecision === opt.value}
                            onChange={() => setExclusionDecision(opt.value)}
                            style={{ accentColor: opt.color }}
                          />
                          <span style={{ fontSize: 13, fontWeight: exclusionDecision === opt.value ? 700 : 400, color: exclusionDecision === opt.value ? opt.color : 'var(--primary)' }}>
                            {opt.label}
                          </span>
                        </label>
                      ))}
                    </div>
                  </div>

                  <div style={{ marginBottom: 16 }}>
                    <label style={{ fontSize: 13, fontWeight: 700, color: 'var(--primary)', display: 'block', marginBottom: 6 }}>
                      Indoklás <span style={{ color: '#c0392b' }}>*</span>
                    </label>
                    <textarea
                      value={exclusionReason}
                      onChange={(e) => setExclusionReason(e.target.value)}
                      placeholder="Kötelező indoklás — rögzítve az auditaplóban és elküldve a pályázónak..."
                      rows={4}
                      style={{
                        width: '100%', padding: '10px 12px', boxSizing: 'border-box',
                        border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)',
                        fontSize: 13, fontFamily: 'var(--font-sans)', lineHeight: 1.6, resize: 'vertical',
                      }}
                    />
                  </div>

                  {exclusionError && (
                    <div style={{ color: '#b91c1c', fontSize: 13, marginBottom: 12 }}>{exclusionError}</div>
                  )}
                  {exclusionSuccess && (
                    <div style={{ color: '#166534', fontSize: 13, marginBottom: 12 }}>{exclusionSuccess}</div>
                  )}

                  <button
                    className="btn btn-primary btn-sm"
                    disabled={!exclusionDecision || !exclusionReason.trim() || exclusionSaving}
                    onClick={() => void handleExclusionDecide()}
                  >
                    {exclusionSaving ? 'Mentés...' : 'Döntés mentése'}
                  </button>
                </>
              )}
            </div>
          )}

          {/* Review scores card */}
          {app.reviewScores && app.reviewScores.length > 0 && (
            <div style={{ background: '#fff', border: '1px solid var(--border)', borderRadius: 'var(--radius-lg)', padding: '24px 28px', boxShadow: 'var(--shadow-sm)', gridColumn: '1 / -1' }}>
              <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '.1em', textTransform: 'uppercase', color: 'var(--secondary-foreground)', marginBottom: 16 }}>Bíralói pontozás</div>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead>
                  <tr style={{ background: 'var(--muted)' }}>
                    <th style={{ padding: '8px 12px', textAlign: 'left', fontWeight: 700, color: 'var(--secondary-foreground)' }}>Bírálo</th>
                    <th style={{ padding: '8px 12px', textAlign: 'right', fontWeight: 700, color: 'var(--secondary-foreground)' }}>Összpontszám</th>
                    <th style={{ padding: '8px 12px', textAlign: 'right', fontWeight: 700, color: 'var(--secondary-foreground)' }}>Pontozva</th>
                    <th style={{ padding: '8px 12px', textAlign: 'center', fontWeight: 700, color: 'var(--secondary-foreground)' }}>Állapot</th>
                  </tr>
                </thead>
                <tbody>
                  {app.reviewScores.map((r: ReviewerScoreSummary) => (
                    <tr key={r.reviewerId} style={{ borderBottom: '1px solid var(--border)' }}>
                      <td style={{ padding: '10px 12px', color: 'var(--primary)' }}>{r.reviewerEmail}</td>
                      <td style={{ padding: '10px 12px', textAlign: 'right', fontWeight: 700, color: r.scoredCount > 0 ? 'var(--primary)' : 'var(--muted-foreground)' }}>
                        {r.scoredCount > 0 ? `${r.totalScore} / ${r.maxScore}` : '—'}
                      </td>
                      <td style={{ padding: '10px 12px', textAlign: 'right', color: 'var(--muted-foreground)' }}>
                        {r.scoredCount} kriterium
                      </td>
                      <td style={{ padding: '10px 12px', textAlign: 'center' }}>
                        <span style={{
                          fontSize: 11, fontWeight: 700, padding: '2px 10px', borderRadius: 12,
                          background: r.finalized ? '#e8f8ee' : r.scoredCount > 0 ? '#fef9e7' : '#f4f6f4',
                          color: r.finalized ? '#1a7a40' : r.scoredCount > 0 ? '#b07d00' : 'var(--muted-foreground)',
                        }}>
                          {r.finalized ? 'Vegleges' : r.scoredCount > 0 ? 'Folyamatban' : 'Nem kezdte'}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {/* Overall total across all finalized reviewers */}
              {app.reviewScores.some((r: ReviewerScoreSummary) => r.finalized) && (() => {
                const finalized = app.reviewScores.filter((r: ReviewerScoreSummary) => r.finalized);
                const avgScore = Math.round(finalized.reduce((s: number, r: ReviewerScoreSummary) => s + r.totalScore, 0) / finalized.length);
                const avgMax   = finalized[0]?.maxScore ?? 0;
                return (
                  <div style={{ marginTop: 16, paddingTop: 16, borderTop: '1px solid var(--border)', display: 'flex', gap: 24 }}>
                    <div>
                      <div style={{ fontSize: 11, color: 'var(--muted-foreground)', marginBottom: 2 }}>Átlag (végleges bírák)</div>
                      <div style={{ fontSize: 18, fontWeight: 700, color: 'var(--primary)' }}>{avgScore} / {avgMax}</div>
                    </div>
                    <div>
                      <div style={{ fontSize: 11, color: 'var(--muted-foreground)', marginBottom: 2 }}>Véglegesített bírák</div>
                      <div style={{ fontSize: 18, fontWeight: 700, color: '#1a7a40' }}>{finalized.length} / {app.reviewScores.length}</div>
                    </div>
                  </div>
                );
              })()}
            </div>
          )}

          {/* Excellent applicant card — shown when suggested */}
          {app.excellentSuggested && (
            <div style={{
              background: '#fff',
              border: '2px solid ' + (app.excellentApproved === true ? '#6ee7b7' : app.excellentApproved === false ? '#d1d5db' : '#fde68a'),
              borderRadius: 'var(--radius-lg)',
              padding: '24px 28px',
              boxShadow: 'var(--shadow-sm)',
              gridColumn: '1 / -1',
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
                <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '.1em', textTransform: 'uppercase', color: 'var(--secondary-foreground)' }}>
                  Kiváló pályázó jelölés
                </div>
                {app.excellentApproved === null && (
                  <span style={{ fontSize: 12, fontWeight: 700, padding: '3px 10px', borderRadius: 12, background: '#fef3c7', color: '#92400e', border: '1px solid #fde68a' }}>⭐ Jelölt — döntés függőben</span>
                )}
                {app.excellentApproved === true && (
                  <span style={{ fontSize: 12, fontWeight: 700, padding: '3px 10px', borderRadius: 12, background: '#d1fae5', color: '#065f46', border: '1px solid #6ee7b7' }}>✨ Kiváló — elfogadva</span>
                )}
                {app.excellentApproved === false && (
                  <span style={{ fontSize: 12, fontWeight: 700, padding: '3px 10px', borderRadius: 12, background: '#f3f4f6', color: '#6b7280', border: '1px solid #d1d5db' }}>⭐ Elutasítva</span>
                )}
              </div>
              <p style={{ fontSize: 13, color: 'var(--muted-foreground)', margin: '0 0 16px' }}>
                A rendszer kiváló pályázónak jelölte. Az elnökség döntést hozhat az elfogadásról vagy elutasításról.
              </p>
              {excellentError && (
                <div style={{ fontSize: 13, color: '#b91c1c', marginBottom: 12 }}>{excellentError}</div>
              )}
              <div style={{ display: 'flex', gap: 10 }}>
                <button
                  className="btn btn-sm"
                  disabled={excellentSaving || app.excellentApproved === true}
                  onClick={() => void handleExcellentDecision(true)}
                  style={{ background: '#d1fae5', color: '#065f46', border: '1px solid #6ee7b7', fontWeight: 700, opacity: app.excellentApproved === true ? 0.6 : 1 }}
                >
                  {excellentSaving ? '...' : '✨ Elfogad'}
                </button>
                <button
                  className="btn btn-sm"
                  disabled={excellentSaving || app.excellentApproved === false}
                  onClick={() => void handleExcellentDecision(false)}
                  style={{ background: '#f3f4f6', color: '#6b7280', border: '1px solid #d1d5db', fontWeight: 700, opacity: app.excellentApproved === false ? 0.6 : 1 }}
                >
                  {excellentSaving ? '...' : 'Elutasít'}
                </button>
                {app.excellentApproved !== null && (
                  <button
                    className="btn btn-ghost btn-sm"
                    disabled={excellentSaving}
                    onClick={() => void handleExcellentDecision(null)}
                    style={{ fontSize: 12, color: 'var(--muted-foreground)' }}
                  >
                    ↺ Visszavon
                  </button>
                )}
              </div>
            </div>
          )}

          {/* Compliance checks card */}
          {complianceLoaded && (
            <div style={{ background: '#fff', border: '1px solid var(--border)', borderRadius: 'var(--radius-lg)', padding: '24px 28px', boxShadow: 'var(--shadow-sm)', gridColumn: '1 / -1' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
                <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '.1em', textTransform: 'uppercase', color: 'var(--secondary-foreground)' }}>Megfelelőségi ellenőrzések</div>
                <button
                  className="btn btn-ghost btn-sm"
                  onClick={() => void handleComplianceRecheck()}
                  disabled={complianceRechecking}
                  style={{ fontSize: 12 }}
                >
                  {complianceRechecking ? 'Ellenőrzés folyamatban…' : '↺ Újra ellenőriz'}
                </button>
              </div>
              {complianceError && (
                <div style={{ fontSize: 13, color: '#b91c1c', marginBottom: 12 }}>{complianceError}</div>
              )}
              {!compliance ? (
                <div style={{ fontSize: 13, color: 'var(--muted-foreground)' }}>
                  {complianceRechecking ? 'Ellenőrzés folyamatban, kérjük várjon…' : 'Még nem fut le ellenőrzés (pályázat beküldése után indul automatikusan).'}
                </div>
              ) : (
                <>
                  <div style={{ fontSize: 11, color: 'var(--muted-foreground)', marginBottom: 12 }}>
                    Legutóbb: {new Date(compliance.ranAt).toLocaleString('hu-HU')}
                    {compliance.ranByUserId ? ' (kézi)' : ' (automatikus)'}
                  </div>
                  {([
                    { key: 'nav', label: 'NAV Köztartozásmentes', status: compliance.navStatus, detail: compliance.navDetail },
                    { key: 'rmk', label: 'RMK (OMMF)',            status: compliance.rmkStatus, detail: compliance.rmkDetail },
                    { key: 'nrmk', label: 'NRMK (NAV)',           status: compliance.nrmkStatus, detail: compliance.nrmkDetail },
                  ] as const).map(({ key, label, status, detail }) => {
                    const cfg = status === 'clean'
                      ? { bg: '#d1fae5', color: '#065f46', icon: '✓', text: 'Rendben' }
                      : status === 'dirty'
                      ? { bg: '#fee2e2', color: '#991b1b', icon: '✗', text: 'Jelölt' }
                      : { bg: '#fef3c7', color: '#92400e', icon: '!', text: 'Hiba' };
                    return (
                      <div key={key} style={{ display: 'flex', alignItems: 'flex-start', gap: 12, marginBottom: 10 }}>
                        <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 22, height: 22, borderRadius: 11, background: cfg.bg, color: cfg.color, fontWeight: 700, fontSize: 12, flexShrink: 0, marginTop: 1 }}>
                          {cfg.icon}
                        </span>
                        <div>
                          <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--primary)' }}>{label}</span>
                          <span style={{ fontSize: 12, fontWeight: 700, color: cfg.color, marginLeft: 8, padding: '1px 7px', borderRadius: 10, background: cfg.bg }}>{cfg.text}</span>
                          {detail && (
                            <div style={{ fontSize: 12, color: 'var(--muted-foreground)', marginTop: 2 }}>{detail}</div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </>
              )}
            </div>
          )}

          {/* Uploads card — general (non-section) files only */}
          {uploadsLoaded && (() => {
            const regularUploads = uploads.filter((u) => u.type !== 'section_file');
            if (regularUploads.length === 0) return null;
            return (
              <div style={{ background: '#fff', border: '1px solid var(--border)', borderRadius: 'var(--radius-lg)', padding: '24px 28px', boxShadow: 'var(--shadow-sm)', gridColumn: '1 / -1' }}>
                <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '.1em', textTransform: 'uppercase', color: 'var(--secondary-foreground)', marginBottom: 16 }}>Mellékelt fájlok</div>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                  <thead>
                    <tr style={{ borderBottom: '1px solid var(--border)' }}>
                      <th style={{ textAlign: 'left', padding: '6px 8px', color: 'var(--secondary-foreground)', fontWeight: 700, fontSize: 11 }}>Típus</th>
                      <th style={{ textAlign: 'left', padding: '6px 8px', color: 'var(--secondary-foreground)', fontWeight: 700, fontSize: 11 }}>Fájlnév</th>
                      <th style={{ textAlign: 'left', padding: '6px 8px', color: 'var(--secondary-foreground)', fontWeight: 700, fontSize: 11 }}>Méret</th>
                      <th style={{ textAlign: 'left', padding: '6px 8px', color: 'var(--secondary-foreground)', fontWeight: 700, fontSize: 11 }}>Feltöltve</th>
                      <th style={{ padding: '6px 8px' }} />
                    </tr>
                  </thead>
                  <tbody>
                    {regularUploads.map((u) => (
                      <tr key={u.id} style={{ borderBottom: '1px solid var(--border)' }}>
                        <td style={{ padding: '8px 8px', color: 'var(--muted-foreground)' }}>{UPLOAD_TYPE_LABELS[u.type] ?? u.type}</td>
                        <td style={{ padding: '8px 8px', color: 'var(--primary)', maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{u.fileName}</td>
                        <td style={{ padding: '8px 8px', color: 'var(--muted-foreground)' }}>
                          {u.fileSize != null ? `${(u.fileSize / 1024).toFixed(0)} KB` : '—'}
                        </td>
                        <td style={{ padding: '8px 8px', color: 'var(--muted-foreground)' }}>
                          {new Date(u.uploadedAt).toLocaleDateString('hu-HU')}
                        </td>
                        <td style={{ padding: '8px 8px', textAlign: 'right' }}>
                          {u.downloadUrl ? (
                            <a href={u.downloadUrl} target="_blank" rel="noreferrer" className="btn btn-ghost btn-sm" style={{ fontSize: 12 }}>
                              Letöltés ↓
                            </a>
                          ) : <span style={{ fontSize: 12, color: 'var(--muted-foreground)' }}>—</span>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            );
          })()}

          {/* Form data — read-only view */}
          {app.formData && Object.keys(app.formData).length > 0 && (
            <div style={{ background: '#fff', border: '1px solid var(--border)', borderRadius: 'var(--radius-lg)', padding: '24px 28px', boxShadow: 'var(--shadow-sm)', gridColumn: '1 / -1' }}>
              <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '.1em', textTransform: 'uppercase', color: 'var(--secondary-foreground)', marginBottom: 20 }}>
                Pályázati adatok
              </div>
              {(() => {
                const sortedSections = Object.entries(app.formData).sort(([a], [b]) => a.localeCompare(b));
                const sortedCriteria = [...criteria].sort((a, b) => a.criterionId.localeCompare(b.criterionId));
                let critIdx = 0;
                return sortedSections.map(([section, fields]) => {
                  const isNumbered = /^\d+\.\d+/.test(section);
                  const crit = isNumbered ? (sortedCriteria[critIdx++] ?? null) : null;
                  return (
                    <div key={section} style={{ marginBottom: 24 }}>
                      <div style={{ marginBottom: 8, paddingBottom: 6, borderBottom: '1px solid var(--border)' }}>
                        <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--secondary-foreground)', textTransform: 'uppercase', letterSpacing: '.08em' }}>
                          {section}
                        </div>
                        {crit && (
                          <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--primary)', marginTop: 3 }}>{crit.label}</div>
                        )}
                      </div>
                      {Object.entries(fields).sort(([a], [b]) => a.localeCompare(b)).map(([fieldKey, fieldValue]) => (
                        <div key={fieldKey} style={{ display: 'grid', gridTemplateColumns: '200px 1fr', gap: '8px 12px', alignItems: 'flex-start', padding: '8px 0', borderBottom: '1px solid #f4f6f4' }}>
                          <div style={{ fontSize: 13, color: 'var(--muted-foreground)', paddingTop: 4 }}>{FORM_FIELD_LABELS[fieldKey] ?? fieldKey}</div>
                          <div style={{ fontSize: 13, color: 'var(--primary)', paddingTop: 4, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                            {fieldValue !== null && fieldValue !== '' ? fieldValue : <span style={{ color: 'var(--muted-foreground)', fontStyle: 'italic' }}>—</span>}
                          </div>
                        </div>
                      ))}
                      {/* Section file uploads inline */}
                      {uploadsLoaded && (() => {
                        const secFiles = uploads.filter(
                          (u) => u.type === 'section_file' && (u as { sectionId?: string | null }).sectionId === section,
                        );
                        if (!secFiles.length) return null;
                        return (
                          <div style={{ marginTop: 10, paddingTop: 10, borderTop: '1px solid var(--border)' }}>
                            <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--secondary-foreground)', textTransform: 'uppercase', letterSpacing: '.06em', marginBottom: 6 }}>
                              Mellékletek
                            </div>
                            {secFiles.map((u) => (
                              <div key={u.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '5px 0', borderBottom: '1px solid #f4f6f4', fontSize: 13 }}>
                                <span style={{ color: 'var(--muted-foreground)', flexShrink: 0 }}>📎</span>
                                <span style={{ color: 'var(--primary)', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{u.fileName}</span>
                                {u.fileSize != null && (
                                  <span style={{ color: 'var(--muted-foreground)', flexShrink: 0, fontSize: 12 }}>
                                    {(u.fileSize / 1024).toFixed(0)} KB
                                  </span>
                                )}
                                {u.downloadUrl ? (
                                  <a href={u.downloadUrl} target="_blank" rel="noreferrer" className="btn btn-ghost btn-sm" style={{ fontSize: 12, flexShrink: 0 }}>
                                    Letöltés ↓
                                  </a>
                                ) : <span style={{ width: 80 }} />}
                              </div>
                            ))}
                          </div>
                        );
                      })()}
                    </div>
                  );
                });
              })()}
            </div>
          )}

          {/* Reviewers card */}
          <div style={{ background: '#fff', border: '1px solid var(--border)', borderRadius: 'var(--radius-lg)', padding: '24px 28px', boxShadow: 'var(--shadow-sm)' }}>
            <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '.1em', textTransform: 'uppercase', color: 'var(--secondary-foreground)', marginBottom: 16 }}>Bírálók</div>
            {app.reviewers.length === 0 && !assigning && (
              <p style={{ fontSize: 13, color: 'var(--muted-foreground)', margin: '0 0 12px' }}>Nincs hozzárendelt bíráló.</p>
            )}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: app.reviewers.length > 0 ? 12 : 0 }}>
              {app.reviewers.map((r) => (
                <div key={r.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 12px', background: 'var(--muted)', borderRadius: 'var(--radius-sm)' }}>
                  <span style={{ fontSize: 13, color: 'var(--primary)' }}>{r.email}</span>
                  <button className="btn btn-ghost btn-sm" style={{ padding: '2px 8px', fontSize: 12 }} onClick={() => void handleUnassignReviewer(r.id)}>x</button>
                </div>
              ))}
            </div>
            {assigning ? (
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <select
                  value={selectedReviewerId}
                  onChange={(e) => setSelectedReviewerId(e.target.value)}
                  style={{ flex: 1, height: 34, padding: '0 8px', border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)', fontSize: 13, fontFamily: 'var(--font-sans)' }}
                >
                  <option value="">Válasszon bírálót</option>
                  {availableReviewers.map((r) => (
                    <option key={r.id} value={r.id}>{r.email}</option>
                  ))}
                </select>
                <button className="btn btn-primary btn-sm" onClick={() => void handleAssignReviewer()} disabled={!selectedReviewerId}>Hozzáad</button>
                <button className="btn btn-ghost btn-sm" onClick={() => { setAssigning(false); setSelectedReviewerId(''); }}>x</button>
              </div>
            ) : (
              <button className="btn btn-ghost btn-sm" onClick={() => setAssigning(true)} disabled={availableReviewers.length === 0}>
                + Bíráló hozzárendelése
              </button>
            )}
          </div>

        {/* AI summary card */}
        <div style={{ background: '#fff', border: '1px solid var(--border)', borderRadius: 'var(--radius-lg)', padding: '24px 28px', boxShadow: 'var(--shadow-sm)' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16, flexWrap: 'wrap', gap: 10 }}>
            <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '.1em', textTransform: 'uppercase', color: 'var(--secondary-foreground)' }}>AI összefoglaló</div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              {aiSummary?.isStale && (
                <span style={{ fontSize: 12, color: '#b45309', background: '#fef3c7', border: '1px solid #f59e0b', borderRadius: 'var(--radius)', padding: '3px 8px' }}>
                  ⚠ Pályázat frissült a generálás óta
                </span>
              )}
              {app?.year !== getAppYear() ? (
                <span style={{ fontSize: 12, color: '#6b7280', background: '#f3f4f6', border: '1px solid #d1d5db', borderRadius: 'var(--radius)', padding: '3px 8px' }}>
                  AI elemzés csak az aktuális ({getAppYear()}) pályázati évhez érhető el
                </span>
              ) : (
                <button
                  className="btn btn-ghost btn-sm"
                  onClick={() => void generateAiSummary()}
                  disabled={aiGenerating || (aiSummary !== null && ['pending', 'running'].includes(aiSummary.status))}
                >
                  {aiGenerating ? 'Indítás...' : aiSummary ? 'Újragenerálás' : 'AI elemzés indítása'}
                </button>
              )}
            </div>
          </div>

          {aiError && (
            <p style={{ fontSize: 13, color: '#991b1b', margin: '0 0 10px' }}>{aiError}</p>
          )}

          {(aiGenerating || (aiSummary && ['pending', 'running'].includes(aiSummary.status))) && (
            <p style={{ fontSize: 13, color: 'var(--muted-foreground)', margin: 0 }}>AI elemzés folyamatban…</p>
          )}

          {aiSummary?.status === 'error' && (
            <p style={{ fontSize: 13, color: '#991b1b', margin: 0 }}>Elemzési hiba: {aiSummary.errorMessage}</p>
          )}

          {!aiSummary && aiLoaded && !aiGenerating && (
            <p style={{ fontSize: 13, color: 'var(--muted-foreground)', margin: 0 }}>Még nincs AI összefoglaló ehhez a pályázathoz.</p>
          )}

          {aiSummary?.status === 'done' && aiSummary.data && (() => {
            const d = aiSummary.data as AiSummaryData;
            return (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                <p style={{ fontSize: 13, lineHeight: 1.6, margin: 0 }}>{d.overview}</p>

                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10 }}>
                  {[
                    { label: 'Erősségek', items: d.strengths, color: '#166534', bg: '#f0fdf4', border: '#86efac' },
                    { label: 'Gyengeségek', items: d.weaknesses, color: '#9a3412', bg: '#fff7ed', border: '#fdba74' },
                    { label: 'Kockázatok', items: d.risks, color: '#7c2d12', bg: '#fef2f2', border: '#fca5a5' },
                  ].map(({ label, items, color, bg, border }) => (
                    <div key={label} style={{ background: bg, border: `1px solid ${border}`, borderRadius: 'var(--radius)', padding: '12px 14px' }}>
                      <div style={{ fontSize: 11, fontWeight: 700, color, marginBottom: 6, textTransform: 'uppercase', letterSpacing: '.05em' }}>{label}</div>
                      {items.length === 0 ? (
                        <p style={{ fontSize: 12, color: 'var(--muted-foreground)', margin: 0 }}>—</p>
                      ) : (
                        <ul style={{ margin: 0, paddingLeft: 14, fontSize: 12, color: 'var(--foreground)', lineHeight: 1.6 }}>
                          {items.map((item, i) => <li key={i}>{item}</li>)}
                        </ul>
                      )}
                    </div>
                  ))}
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  {Object.entries(d.sections).sort(([a], [b]) => a.localeCompare(b, undefined, { numeric: true })).map(([sectionId, sec]) => {
                    const isOpen = aiExpanded.has(sectionId);
                    return (
                      <div key={sectionId} style={{ border: '1px solid var(--border)', borderRadius: 'var(--radius)' }}>
                        <button
                          onClick={() => toggleAiSection(sectionId)}
                          style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 14px', background: 'none', border: 'none', cursor: 'pointer', textAlign: 'left' }}
                        >
                          <span style={{ fontSize: 13, fontWeight: 600 }}>{sectionId} – {sec.label}</span>
                          <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--primary)' }}>{sec.suggestedScore} / {sec.maxScore} pt {isOpen ? '▲' : '▼'}</span>
                        </button>
                        {isOpen && (
                          <div style={{ padding: '8px 14px 12px', borderTop: '1px solid var(--border)' }}>
                            {sec.note && <p style={{ fontSize: 12, margin: '0 0 8px', lineHeight: 1.5 }}>{sec.note}</p>}
                            {Object.keys(sec.subcriteria).length > 0 && (
                              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12, marginBottom: 4 }}>
                                <tbody>
                                  {Object.entries(sec.subcriteria).map(([subId, sub]) => (
                                    <tr key={subId} style={{ borderBottom: '1px solid var(--border)' }}>
                                      <td style={{ padding: '4px 8px' }}>{subId}</td>
                                      <td style={{ padding: '4px 8px', textAlign: 'right', fontWeight: 700, color: 'var(--primary)' }}>{sub.score} / {sub.max}</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })()}
        </div>

        </div>
      </div>
    </AdminLayout>
  );
}
